import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Wand2, Sparkles, ShieldCheck, X as XIcon,
  RefreshCw, Search, Loader2, Lightbulb, Check, Ban,
} from 'lucide-react';

/**
 * Bank Rules Management (Feb 2026).
 *
 * Three sections:
 *   1) Active rules — priority-ordered, editable.
 *   2) Rule Simulator — "Test Rule" panel that previews which rule
 *      fires for a sample description.
 *   3) Learning Suggestions — patterns the user has manually matched
 *      ≥ 3 times. One-click "اعتِماد كَقاعِدَة".
 */

const ACTION_AR = {
  supplier:          'مَوَرِّد',
  category:          'فِئَة مَصاريف',
  account:           'حِساب GL',
  payment_method:    'طَريقة دَفع',
  government_entity: 'جِهَة حُكومية',
  cost_center:       'مَركَز تَكلِفَة',
};

const PATTERN_AR = {
  contains:  'يَحتَوي',
  regex:     'Regex',
  exact:     'مُطابِق تَماماً',
  sadad:     'رَقم سَداد',
  iban_tail: 'IBAN (آخِر أَرقام)',
};


function RuleEditor({ initial, onClose, onSaved, meta }) {
  const [form, setForm] = useState(() => ({
    name:               initial?.name || '',
    pattern:            initial?.pattern || '',
    pattern_type:       initial?.pattern_type || 'contains',
    priority:           initial?.priority ?? 60,
    action_kind:        initial?.action_kind || 'supplier',
    action_value_id:    initial?.action_value_id || '',
    action_value_label: initial?.action_value_label || '',
    enabled:            initial?.enabled ?? true,
  }));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.pattern.trim()) {
      toast.error('الاسم والنَّمَط مَطلوبان');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form,
        action_value_id: form.action_value_id || null,
        action_value_label: form.action_value_label || null };
      if (initial?.id) {
        await axiosInstance.patch(`/bank-rules/${initial.id}`, payload);
        toast.success('تَم تَحديث القاعِدَة');
      } else {
        await axiosInstance.post('/bank-rules', payload);
        toast.success('تَمَّت إضافَة القاعِدَة');
      }
      onSaved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'فَشَل الحِفظ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-lg shadow-2xl"
           data-testid="rule-editor">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">{initial?.id ? 'تَعديل قاعِدَة' : 'قاعِدَة جَديدَة'}</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">الاسم</label>
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                   data-testid="rule-name"
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600">نَوع النَّمَط</label>
              <select value={form.pattern_type} onChange={(e) => setForm({...form, pattern_type: e.target.value})}
                      data-testid="rule-pattern-type"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                {(meta?.pattern_types || Object.keys(PATTERN_AR)).map(k => (
                  <option key={k} value={k}>{PATTERN_AR[k] || k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">الأَولَوِيَّة</label>
              <input type="number" min={0} max={100}
                     value={form.priority} onChange={(e) => setForm({...form, priority: +e.target.value})}
                     data-testid="rule-priority"
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">النَّمَط</label>
            <input value={form.pattern} onChange={(e) => setForm({...form, pattern: e.target.value})}
                   data-testid="rule-pattern"
                   placeholder="مَثَلاً: STCPAY, SADAD, MADA POS"
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600">نَوع الإجراء</label>
              <select value={form.action_kind} onChange={(e) => setForm({...form, action_kind: e.target.value})}
                      data-testid="rule-action-kind"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                {(meta?.action_kinds || Object.keys(ACTION_AR)).map(k => (
                  <option key={k} value={k}>{ACTION_AR[k] || k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">المُعَرِّف (اختِياري)</label>
              <input value={form.action_value_id} onChange={(e) => setForm({...form, action_value_id: e.target.value})}
                     className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                     placeholder="UUID" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">عُنوان الإجراء (للعَرض)</label>
            <input value={form.action_value_label} onChange={(e) => setForm({...form, action_value_label: e.target.value})}
                   className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                   placeholder="مَثَلاً: شَركة الكَهرَباء" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled}
                   onChange={(e) => setForm({...form, enabled: e.target.checked})}
                   data-testid="rule-enabled" />
            مُفَعَّل
          </label>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-white">إلغاء</button>
          <button onClick={save} disabled={busy} data-testid="save-rule-btn"
                  className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'حِفظ'}
          </button>
        </div>
      </div>
    </div>
  );
}


function Simulator() {
  const [desc, setDesc] = useState('');
  const [sadad, setSadad] = useState('');
  const [iban, setIban] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post('/bank-rules/simulate',
        { description: desc, sadad, iban });
      setResult(data);
    } catch (e) {
      toast.error('فَشَلَت المُحاكاة');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="rule-simulator">
      <div className="flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-indigo-600" />
        <div className="font-semibold text-sm">Rule Simulator — تَجرِبَة القَواعِد</div>
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <input value={desc} onChange={(e) => setDesc(e.target.value)}
               data-testid="sim-desc"
               placeholder="وَصف الحَرَكة (مَثَلاً: SADAD 123456 وزارة)"
               className="md:col-span-3 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <input value={sadad} onChange={(e) => setSadad(e.target.value)}
               data-testid="sim-sadad"
               placeholder="رَقم سَداد (اختِياري)"
               className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
        <input value={iban} onChange={(e) => setIban(e.target.value)}
               data-testid="sim-iban"
               placeholder="IBAN (اختِياري)"
               className="md:col-span-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
      </div>
      <button onClick={run} disabled={busy || !desc.trim()}
              data-testid="sim-run-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        تَشغيل
      </button>
      {result && (
        <div className={`rounded-lg border p-3 text-sm ${
          result.matched ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
        }`} data-testid="sim-result">
          {result.matched ? (
            <div className="space-y-1">
              <div className="font-semibold text-emerald-800">✓ القاعِدَة فَعَّلَت: {result.rule_name}</div>
              <div className="text-xs grid grid-cols-2 gap-y-1">
                <div>الأَولَوِيَّة: <span className="font-mono">{result.priority}</span></div>
                <div>نَوع الإجراء: <span className="font-semibold">{ACTION_AR[result.action_kind] || result.action_kind}</span></div>
                <div>النَّمَط: <span className="font-mono">{result.pattern}</span> ({PATTERN_AR[result.pattern_type]})</div>
                <div>القيمَة: <span className="font-semibold">{result.action_value_label || result.action_value_id || '—'}</span></div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                المِفتاح المُطَبَّع: <span className="font-mono">{result.normalized}</span>
              </div>
            </div>
          ) : (
            <div className="text-amber-800">
              ✗ لا تُوجَد قاعِدَة مُطابِقَة. المِفتاح المُطَبَّع: <span className="font-mono">{result.normalized}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SuggestionsPanel({ items, threshold, onRefresh }) {
  const act = async (id, action) => {
    try {
      await axiosInstance.post(`/bank-rules/suggestions/${id}/${action}`);
      toast.success(action === 'approve' ? 'تَم اعتِماد القاعِدَة' : 'تَم الرَفض');
      onRefresh();
    } catch (e) {
      toast.error('فَشَل');
    }
  };

  return (
    <div className="rounded-xl border bg-white">
      <div className="px-4 py-2.5 border-b text-sm font-semibold text-slate-700 flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-amber-500" />
        اقتِراحات مُتَعَلَّمَة (≥ {threshold} مَرَّات يَدَوياً)
        <span className="text-slate-400">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">
          لا تُوجَد اقتِراحات حالياً. سَتَظهَر هُنا أَنماط مُتَكَرِّرَة لاعتِمادِها كَقَواعِد.
        </div>
      ) : (
        <div className="divide-y">
          {items.map((s) => (
            <div key={s.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`suggestion-${s.id}`}>
              <div className="flex-1">
                <div className="font-mono text-sm">{s.normalized}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  → {ACTION_AR[s.action_kind]}: <span className="font-semibold">{s.action_value_label || s.action_value_id}</span>
                  {' · '}{s.occurrences} مَرَّات
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => act(s.id, 'approve')}
                        data-testid={`approve-suggestion-${s.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
                  <Check className="w-3.5 h-3.5" /> اعتِماد
                </button>
                <button onClick={() => act(s.id, 'dismiss')}
                        data-testid={`dismiss-suggestion-${s.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-semibold">
                  <Ban className="w-3.5 h-3.5" /> رَفض
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function BankRules() {
  const [meta, setMeta] = useState(null);
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: sug }] = await Promise.all([
        axiosInstance.get('/bank-rules'),
        axiosInstance.get('/bank-rules/suggestions'),
      ]);
      setRules(r.items || []);
      setMeta({
        pattern_types:    r.pattern_types,
        action_kinds:     r.action_kinds,
        priority_presets: r.priority_presets,
      });
      setSuggestions(sug.items || []);
      setThreshold(sug.threshold || 3);
    } catch (e) {
      toast.error('تَعَذَّر التَحميل');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`حَذف القاعِدَة "${r.name}"؟`)) return;
    try {
      await axiosInstance.delete(`/bank-rules/${r.id}`);
      toast.success('تَم الحَذف');
      load();
    } catch (e) {
      toast.error('فَشَل الحَذف');
    }
  };

  const filtered = useMemo(() => {
    if (!search) return rules;
    const q = search.toLowerCase();
    return rules.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.pattern || '').toLowerCase().includes(q) ||
      (r.action_value_label || '').toLowerCase().includes(q)
    );
  }, [rules, search]);

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-indigo-600" />
            قَواعِد المُطابَقة البَنكية
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            اربط الأَنماط بالمَوَرِّدين والفِئات. القَواعِد ذات الأَولَوِيَّة الأَعلى تَفوز.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setEditing({})}
                  data-testid="new-rule-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-2">
            <Plus className="w-4 h-4" /> قاعِدَة جَديدَة
          </button>
        </div>
      </div>

      <Simulator />

      <div className="rounded-xl border bg-white">
        <div className="px-4 py-2.5 border-b flex items-center justify-between gap-2 flex-wrap">
          <div className="font-semibold text-sm">القَواعِد النَّشِطَة <span className="text-slate-400">({rules.length})</span></div>
          <div className="relative">
            <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   data-testid="rules-search"
                   placeholder="بَحث…" className="ps-7 pe-3 py-1.5 text-sm rounded-lg border border-slate-300 w-56" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-start px-3 py-2">الأَولَوِيَّة</th>
                <th className="text-start px-3 py-2">الاسم</th>
                <th className="text-start px-3 py-2">النَّمَط</th>
                <th className="text-start px-3 py-2">الإجراء</th>
                <th className="text-end px-3 py-2"># تَفعيل</th>
                <th className="text-start px-3 py-2">المَصدَر</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">لا تُوجَد قَواعِد. أَنشِئ قاعِدَة أَو اعتَمِد اقتِراحاً.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className={`border-t hover:bg-slate-50/60 ${!r.enabled ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2 font-mono font-bold">{r.priority}</td>
                  <td className="px-3 py-2">
                    <div>{r.name}</div>
                    {!r.enabled && <span className="text-[10px] text-zinc-500">— مُعَطَّلَة</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] uppercase text-slate-400 me-1">{PATTERN_AR[r.pattern_type] || r.pattern_type}</span>
                    <span className="font-mono text-xs">{r.pattern}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] text-slate-500">{ACTION_AR[r.action_kind]}: </span>
                    <span className="font-semibold">{r.action_value_label || r.action_value_id || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-end font-mono text-xs">{r.match_count || 0}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.source === 'learned' ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"><Lightbulb className="w-3 h-3" /> مُتَعَلَّمَة</span>
                    ) : (
                      <span className="text-slate-500">يَدَوية</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditing(r)}
                              data-testid={`edit-rule-${r.id}`}
                              className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r)}
                              data-testid={`delete-rule-${r.id}`}
                              className="p-1.5 rounded-md bg-red-100 hover:bg-red-200 text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SuggestionsPanel items={suggestions} threshold={threshold} onRefresh={load} />

      {editing !== null && (
        <RuleEditor initial={editing.id ? editing : null}
                    meta={meta}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
